import React, { createContext, useContext, useEffect } from "react";
import { Form as FinalForm } from "react-final-form";
import { useLocation, useParams } from "react-router";
import AppBar from "src/components/AppBar/AppBar";
import { IconName } from "src/components/Icon/Icon";
import * as S from "../../constants/StringConstants";
import { Loading } from "./components/loading";
import { renderOption, RenderOptionValues } from "./components/options";
import { BarWidget, FigureWidget, GaugeWidget, LineWidget, PieWidget, TableWidget } from "./components/widgets";
import { defaultChartColors } from "./constants";
import { ReportConfig, ReportItemBase, ReportRowConfig } from "./definitions";
import allReports from "./docs";
import { buildInitialOptions, handleDownload, mergeQueryParams, range } from "./utils";

import { Link } from "react-router-dom";
import { useInterval } from "src/hooks/useInterval";
import { usePageTitle } from "src/hooks/usePageTitle";
import "../../components/NavTabs/Tabs";
import { formatCADateTime } from "../CampaignDetail/utils";
import { RendererInterface } from "./components/widgets/types";
import "./ReportDetail.scss";

const KIOSK_REFRESH_INTERVAL_MS = 15 * 60 * 1000;

const allRenderers: Record<string, RendererInterface> = {
  figure: FigureWidget,
  table: TableWidget,
  pie: PieWidget,
  line: LineWidget,
  bar: BarWidget,
  gauge: GaugeWidget,
};

const renderCol = (col: ReportItemBase, index: number, row: ReportRowConfig, optionValues: Record<string, string>) => {
  const colCount = row.items.length;

  const Renderer: RendererInterface = allRenderers[col.widgetType];
  let className = "";

  switch (colCount) {
    case 1:
      className = "full";
      break;
    case 2:
      className = "half";
      break;
    case 3:
      if (col.options?.grow) {
        className = "half";
      } else if (col.options?.singleCol) {
        className = "quarter";
      } else {
        className = "third";
      }
      break;
  }
  const hasTitle = !!col.title;
  const hasTable = col.widgetType === "table";
  if (hasTitle) {
    className += " has-title";
  }

  if (hasTable) {
    className += " has-table";
  }
  return (
    <div className={`report__col report__card ${className}`} key={index}>
      {hasTitle ? <div className="report__card_title">{col.title}</div> : null}
      <Renderer
        config={col}
        colors={defaultChartColors}
        optionValues={optionValues}
        loadingIndicator={<Loading />}
        errorDisplay={<div>Error</div>}
      />
    </div>
  );
};

const renderDyn = (
  row: ReportRowConfig,
  col: ReportItemBase,
  dynOption: any,
  index: number,
  optionValues: Record<string, string | Object>,
) => {
  // const dyn//Option = optionValues[row.dynamicItemsBy][index] as any;
  const Renderer: RendererInterface = allRenderers[col.widgetType];

  const newOptionValues = Object.keys(optionValues).reduce((acc, key) => {
    // copy all string values
    if (typeof optionValues[key] === "string") {
      acc[key] = optionValues[key];
    }
    // for dynamic field, copy only current value
    if (key === row.dynamicItemsBy) {
      acc[key] = dynOption.value;
    }
    return acc;
  }, {});

  let title = [col.title, dynOption.key].filter(x => !!x).join(" - ");
  return (
    <div className="report__col report__card" key={index}>
      {title ? <div className="report__card_title">{title}</div> : null}
      <Renderer
        config={{ ...col, title }}
        colors={defaultChartColors}
        optionValues={newOptionValues}
        loadingIndicator={<Loading />}
        errorDisplay={<div>Error</div>}
      />
    </div>
  );
};

const renderRow = (row: ReportRowConfig, index: number, optionValues: Record<string, string>) => {
  let dynamicItems = [];
  if (row.dynamicItemsBy) {
    dynamicItems = (optionValues[row.dynamicItemsBy] as any) || [];
  }
  return (
    <div
      className={`report__row
          ${row.dynamicItemsBy ? "dynamic" : null}
          ${row.dynamicItemsBy && row.dynamicOptions?.fullWidth ? "fullWidth" : null}`}
      key={index}>
      {row.dynamicItemsBy ? (
        <>
          {row.dynamicItemsEmptyMessage && dynamicItems.length === 0 ? (
            <div className="empty-dynamic-row">{row.dynamicItemsEmptyMessage}</div>
          ) : null}
          {dynamicItems.map((dyn, index) => renderDyn(row, row.items[0], dyn, index, optionValues))}
        </>
      ) : (
        row.items.map((col, index) => renderCol(col, index, row, optionValues))
      )}
    </div>
  );
};

interface ReportRendererProps {
  report: ReportConfig;
  optionValues: Record<string, string>;
  renderOptions?: boolean;
}

export const ReportRenderer: React.FC<ReportRendererProps> = React.memo(
  ({ report, optionValues, renderOptions = true }) => {
    const { widgetData } = useContext(ReportDataContext);

    let renderer = () => report.layout.map((row, index) => renderRow(row, index, optionValues));

    usePageTitle(S.REPORT_TITLE(report.name));

    const { config = {} } = report;
    const isRepeat = config.repeat;
    if (isRepeat) {
      const { name, min, max, step } = config.repeat;
      renderer = () => {
        return range(min, max, step).map(i => {
          return (
            <div className="report__repeat" key={i}>
              {report.layout.map((row, index) => renderRow(row, index, { ...optionValues, [name]: i }))}
            </div>
          );
        });
      };
    }

    useEffect(() => {
      const bodyClass = report.print?.bodyClass;
      if (bodyClass) {
        document.body.classList.add(bodyClass);
        return () => {
          document.body.classList.remove(bodyClass);
        };
      }
    }, []);

    return (
      <div className="report__wrapper">
        <div className="report__container">
          <div className="report__print_header screen-hidden">
            <div className="left">
              <div className="title">{report.name}</div>
              <div className="date">
                {new Date().toLocaleString("en-US", {
                  timeZone: "America/Los_Angeles",
                })}
              </div>
            </div>
            <div className="option-values">
              <RenderOptionValues sourceOptions={report.options} values={optionValues} />
            </div>
          </div>

          <div className="report__header report__card print-hidden">
            <div className="report__row report__title-row">
              <div className="report__col report__title full">
                {report.name}
                <div className="report__date">{formatCADateTime(new Date().toString())}</div>
              </div>
            </div>
            {report.description && (
              <div className="report__row">
                <div className="report__col report__description full">{report.description}</div>
              </div>
            )}
            {renderOptions && (
              <div className="report__row report__options print-hidden">
                <div className="report__option-items">{report.options.map(renderOption)}</div>
                <div className="report__actions">
                  <button
                    className="button-v2 button-v2__secondary small mt-5"
                    onClick={() => handleDownload(report, widgetData)}>
                    {S.REPORTS_DOWNLOAD_BUTTON}
                  </button>
                  <button className="button-v2 button-v2__primary small mt-5" onClick={() => window.print()}>
                    {S.REPORTS_PRINT_BUTTON}
                  </button>
                </div>
              </div>
            )}
          </div>
          {renderer()}
        </div>
      </div>
    );
  },
);

export const ReportDataContext = createContext(null);

export const ReportDetailBare = (_props: ReportsDetailProps) => {
  const { id } = useParams<{ id: string }>();
  const report = allReports.find(rep => rep.id === id);
  const initialOptions = buildInitialOptions(report.options);
  mergeQueryParams(initialOptions);
  const contextValue = {
    widgetData: null,
    updateWidgetData: (_widgetId: string, _data: any, _columns: any) => {},
  };
  return (
    <ReportDataContext.Provider value={contextValue}>
      <ReportRenderer report={report} optionValues={initialOptions} renderOptions={false} />;
    </ReportDataContext.Provider>
  );
};

export const ReportDetailKiosk = (_props: ReportsDetailProps) => {
  const { id } = useParams<{ id: string }>();
  const report = allReports.find(rep => rep.id === id);
  const initialOptions = buildInitialOptions(report.options);
  mergeQueryParams(initialOptions);
  const contextValue = {
    widgetData: null,
    updateWidgetData: (_widgetId: string, _data: any, _columns: any) => {},
  };

  const [lastUpdated, setLastUpdated] = React.useState<Date>(new Date());

  useInterval(() => {
    const timeSinceLastUpdate = new Date().getTime() - lastUpdated.getTime();
    if (timeSinceLastUpdate >= KIOSK_REFRESH_INTERVAL_MS) {
      setLastUpdated(new Date());
      // perhaps reloading is better, similar result and may avoid memory leaks if any
      // window.location.reload();
    }
  }, 15 * 1000);

  return (
    <div className="kiosk-container" key={lastUpdated.toISOString()}>
      <ReportDataContext.Provider value={contextValue}>
        <ReportRenderer report={report} optionValues={initialOptions} renderOptions={false} />;
      </ReportDataContext.Provider>
    </div>
  );
};

interface ReportsDetailProps {}

export const ReportDetail = (_props: ReportsDetailProps) => {
  const locationState = useLocation<{ state: any }>();

  const folder = (locationState.state as any)?.folder;
  const siblingReports = folder?.reports ?? [];
  const { id } = useParams<{ id: string }>();

  const submitForm = (_values: any) => {
    // no-op
  };

  const report = allReports.find(rep => rep.id === id);

  const initialOptions = buildInitialOptions(report.options);
  mergeQueryParams(initialOptions);

  const widgetDataRref = React.useRef({});
  const contextValue = {
    widgetData: widgetDataRref.current,
    updateWidgetData: (widgetId: string, data: any, columns: any) => {
      if (widgetId) {
        widgetDataRref.current[widgetId] = [data, columns];
      }
    },
  };

  return (
    <div className="page-container">
      <AppBar icon={IconName.Reports} title={folder?.title ?? S.REPORTS_TITLE} />
      {siblingReports.length > 0 && (
        <div className="nav-tabs print-hidden">
          {siblingReports.map(report => (
            <Link
              key={report.id}
              className={id === report.id ? "active" : ""}
              to={{ pathname: `/reports/${report.id}`, state: { folder } }}>
              {report.name}
            </Link>
          ))}
        </div>
      )}

      <ReportDataContext.Provider value={contextValue}>
        <FinalForm
          onSubmit={submitForm}
          initialValues={initialOptions}
          render={({ values: optionValues }) => <ReportRenderer report={report} optionValues={optionValues} />}
        />
      </ReportDataContext.Provider>
    </div>
  );
};
